/* ============================================================
   ZuhauseTEST – Automatisches Gratisgeschenk
   ============================================================

   Variante:
   57374230249852

   Mindestwarenwert:
   25,00 €

   Funktionen:
   - Geschenk automatisch ab 25 €
   - unter 25 € automatisch entfernen
   - maximal 1 Geschenk
   - Geschenkmenge nicht veränderbar
   - "1 × GRATIS" im Warenkorb
   - Papierkorb beim Geschenk ausblenden
   - PDP-Fortschritt aktualisieren
   ============================================================ */

(() => {
  'use strict';


  /* ============================================================
     KONFIGURATION
     ============================================================ */

  const MINIMUM_CART_VALUE = 2500;

  const GIFT_VARIANT_ID = 57374230249852;

  const CHECK_INTERVAL = 1000;

  const DEBUG = false;


  /* ============================================================
     INTERN
     ============================================================ */

  let isSyncing = false;

  let lastCartSignature = null;


  /* ============================================================
     DEBUG
     ============================================================ */

  function log(...args) {

    if (DEBUG) {
      console.log(
        '[ZT Gratisgeschenk]',
        ...args
      );
    }

  }


  /* ============================================================
     SHOPIFY ROOT
     ============================================================ */

  function getRoot() {

    if (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) {
      return window.Shopify.routes.root;
    }

    return '/';

  }


  /* ============================================================
     GELD FORMATIEREN
     ============================================================ */

  function formatMoney(cents) {

    return new Intl.NumberFormat(
      'de-DE',
      {
        style: 'currency',
        currency: 'EUR'
      }
    ).format(
      Number(cents) / 100
    );

  }


  /* ============================================================
     CART LADEN
     ============================================================ */

  async function getCart() {

    const response = await fetch(
      `${getRoot()}cart.js`,
      {
        method: 'GET',

        headers: {
          'Accept': 'application/json'
        },

        cache: 'no-store'
      }
    );


    if (!response.ok) {

      throw new Error(
        `Warenkorb konnte nicht geladen werden: ${response.status}`
      );

    }


    return response.json();

  }


  /* ============================================================
     GESCHENKPOSITIONEN
     ============================================================ */

  function getGiftItems(cart) {

    return cart.items.filter(
      item =>
        Number(item.variant_id) ===
        Number(GIFT_VARIANT_ID)
    );

  }


  /* ============================================================
     QUALIFIZIERENDER WARENWERT
     ============================================================ */

  function getQualifyingCartValue(cart) {

    return cart.items.reduce(
      (total, item) => {

        if (
          Number(item.variant_id) ===
          Number(GIFT_VARIANT_ID)
        ) {
          return total;
        }


        return (
          total +
          Number(item.final_line_price || 0)
        );

      },
      0
    );

  }


  /* ============================================================
     CART SIGNATUR
     ============================================================ */

  function getCartSignature(cart) {

    return cart.items
      .map(item => {

        return [
          item.key,
          item.quantity,
          item.final_line_price
        ].join(':');

      })
      .sort()
      .join('|');

  }


  /* ============================================================
     PDP GESCHENKBOX
     ============================================================ */

  function updateGiftBoxes(cart) {

    const boxes =
      document.querySelectorAll(
        '[data-zt-free-gift-box]'
      );


    if (!boxes.length) {
      return;
    }


    const cartValue =
      getQualifyingCartValue(cart);


    const qualifies =
      cartValue >=
      MINIMUM_CART_VALUE;


    const remaining =
      Math.max(
        MINIMUM_CART_VALUE - cartValue,
        0
      );


    const progress =
      Math.min(
        (
          cartValue /
          MINIMUM_CART_VALUE
        ) * 100,
        100
      );


    boxes.forEach(box => {

      const status =
        box.querySelector(
          '[data-zt-free-gift-status]'
        );


      const text =
        box.querySelector(
          '[data-zt-free-gift-text]'
        );


      const progressBar =
        box.querySelector(
          '[data-zt-free-gift-progress]'
        );


      if (progressBar) {

        progressBar.style.width =
          `${progress}%`;

      }


      if (qualifies) {

        box.classList.add(
          'is-qualified'
        );


        if (status) {

          status.textContent =
            '✓ Dein Geschenk ist dabei';

        }


        if (text) {

          text.textContent =
            'Wird automatisch deinem Warenkorb hinzugefügt.';

        }


      } else {

        box.classList.remove(
          'is-qualified'
        );


        if (status) {

          status.textContent =
            'Ab 25 € Bestellwert';

        }


        if (text) {

          text.innerHTML =
            `Noch <strong>${formatMoney(remaining)}</strong> bis zu deinem Gratisgeschenk.`;

        }

      }

    });

  }


  /* ============================================================
     CART DRAWER RENDERN
     ============================================================ */

  function getSectionHtml(
    sections,
    sectionId,
    selector
  ) {

    if (
      !sections ||
      !sections[sectionId]
    ) {
      return null;
    }


    const parser =
      new DOMParser();


    const parsed =
      parser.parseFromString(
        sections[sectionId],
        'text/html'
      );


    const element =
      parsed.querySelector(
        selector
      );


    return element
      ? element.innerHTML
      : null;

  }


  function renderCartSections(sections) {

    if (!sections) {
      return;
    }


    if (sections['cart-drawer']) {

      const parser =
        new DOMParser();


      const parsed =
        parser.parseFromString(
          sections['cart-drawer'],
          'text/html'
        );


      const newDrawer =
        parsed.querySelector(
          'cart-drawer'
        );


      const currentDrawer =
        document.querySelector(
          'cart-drawer'
        );


      if (
        newDrawer &&
        currentDrawer
      ) {

        currentDrawer.innerHTML =
          newDrawer.innerHTML;

      }

    }


    if (sections['cart-icon-bubble']) {

      const newBubbleHtml =
        getSectionHtml(
          sections,
          'cart-icon-bubble',
          '#cart-icon-bubble'
        );


      const currentBubble =
        document.querySelector(
          '#cart-icon-bubble'
        );


      if (
        newBubbleHtml !== null &&
        currentBubble
      ) {

        currentBubble.innerHTML =
          newBubbleHtml;

      }

    }


    window.setTimeout(
      lockGiftQuantityControls,
      100
    );

  }


  /* ============================================================
     GESCHENK HINZUFÜGEN
     ============================================================ */

  async function addGift() {

    const response = await fetch(
      `${getRoot()}cart/add.js`,
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/json',

          'Accept':
            'application/json'
        },

        body: JSON.stringify({

          items: [
            {
              id:
                GIFT_VARIANT_ID,

              quantity:
                1,

              properties: {
                '_Gratisgeschenk':
                  'Ja'
              }
            }
          ],

          sections: [
            'cart-drawer',
            'cart-icon-bubble'
          ],

          sections_url:
            window.location.pathname

        })
      }
    );


    const data =
      await response.json();


    if (!response.ok) {

      throw new Error(
        `Gratisgeschenk konnte nicht hinzugefügt werden: ${JSON.stringify(data)}`
      );

    }


    if (data.sections) {

      renderCartSections(
        data.sections
      );

    }


    return data;

  }


  /* ============================================================
     GESCHENK-LINE ÄNDERN
     ============================================================ */

  async function changeGiftLine(
    lineKey,
    quantity
  ) {

    const response = await fetch(
      `${getRoot()}cart/change.js`,
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/json',

          'Accept':
            'application/json'
        },

        body: JSON.stringify({

          id:
            lineKey,

          quantity:
            quantity,

          sections: [
            'cart-drawer',
            'cart-icon-bubble'
          ],

          sections_url:
            window.location.pathname

        })
      }
    );


    const data =
      await response.json();


    if (!response.ok) {

      throw new Error(
        `Gratisgeschenk konnte nicht geändert werden: ${JSON.stringify(data)}`
      );

    }


    if (data.sections) {

      renderCartSections(
        data.sections
      );

    }


    return data;

  }


  /* ============================================================
     GESCHENKMENGE IM SHOPIFY-CART AUF 1 SETZEN
     ============================================================ */

  async function normalizeGiftQuantity(
    giftItems
  ) {

    if (!giftItems.length) {
      return false;
    }


    let changed =
      false;


    const firstGift =
      giftItems[0];


    if (
      Number(firstGift.quantity) !== 1
    ) {

      await changeGiftLine(
        firstGift.key,
        1
      );


      changed =
        true;

    }


    /*
      Falls mehrere Geschenk-Lines vorhanden sind,
      alle zusätzlichen entfernen.
    */

    for (
      let i = 1;
      i < giftItems.length;
      i++
    ) {

      await changeGiftLine(
        giftItems[i].key,
        0
      );


      changed =
        true;

    }


    return changed;

  }


  /* ============================================================
     FESTSTELLEN OB CART ITEM DAS GESCHENK IST
     ============================================================ */

  function isGiftCartItem(item) {

    if (!item) {
      return false;
    }


    const html =
      item.innerHTML || '';


    if (
      html.includes(
        String(GIFT_VARIANT_ID)
      )
    ) {
      return true;
    }


    const giftLink =
      item.querySelector(
        'a[href*="/products/gratisgeschenk"]'
      );


    return Boolean(
      giftLink
    );

  }


  /* ============================================================
     MENGENSTEUERUNG SPERREN
     ============================================================ */

  function lockGiftQuantityControls() {

    const cartItems =
      document.querySelectorAll(
        `
        cart-item,
        .cart-item,
        tr.cart-item,
        [data-cart-item]
        `
      );


    cartItems.forEach(item => {

      if (
        !isGiftCartItem(item)
      ) {
        return;
      }


      item.classList.add(
        'zt-free-gift-cart-item'
      );


      /* ========================================================
         MENGEN-WRAPPER
         ======================================================== */

      const quantityWrappers =
        item.querySelectorAll(
          `
          quantity-input,
          .quantity
          `
        );


      quantityWrappers.forEach(wrapper => {

        /*
          Komplett gegen Benutzerinteraktion sperren.
        */

        wrapper.style.pointerEvents =
          'none';


        wrapper.setAttribute(
          'aria-disabled',
          'true'
        );

      });


      /* ========================================================
         PLUS / MINUS
         ======================================================== */

      const buttons =
        item.querySelectorAll(
          `
          .quantity__button,
          button[name="plus"],
          button[name="minus"],
          [data-quantity-plus],
          [data-quantity-minus]
          `
        );


      buttons.forEach(button => {

        button.disabled =
          true;


        button.style.display =
          'none';


        button.style.pointerEvents =
          'none';


        button.setAttribute(
          'aria-disabled',
          'true'
        );

      });


      /* ========================================================
         INPUT
         ======================================================== */

      const inputs =
        item.querySelectorAll(
          `
          .quantity__input,
          input[name="updates[]"],
          input[type="number"]
          `
        );


      inputs.forEach(input => {

        input.value =
          1;


        input.min =
          1;


        input.max =
          1;


        input.readOnly =
          true;


        input.disabled =
          true;


        input.style.pointerEvents =
          'none';


        input.setAttribute(
          'aria-readonly',
          'true'
        );

      });


      /* ========================================================
         PAPIERKORB AUSBLENDEN
         ======================================================== */

      const removeButtons =
        item.querySelectorAll(
          `
          cart-remove-button,
          .cart-remove-button,
          [id^="Remove-"],
          a[href*="/cart/change"][href*="quantity=0"]
          `
        );


      removeButtons.forEach(button => {

        button.style.display =
          'none';

      });


      /* ========================================================
         "1 × GRATIS" ANZEIGEN
         ======================================================== */

      if (
        !item.querySelector(
          '.zt-free-gift-cart-quantity'
        )
      ) {

        const quantityWrapper =
          item.querySelector(
            'quantity-input, .quantity'
          );


        if (quantityWrapper) {

          quantityWrapper.style.display =
            'none';


          const giftQuantity =
            document.createElement(
              'span'
            );


          giftQuantity.className =
            'zt-free-gift-cart-quantity';


          giftQuantity.textContent =
            '1 × GRATIS';


          quantityWrapper.insertAdjacentElement(
            'afterend',
            giftQuantity
          );

        }

      }

    });

  }


  /* ============================================================
     BENUTZERINTERAKTION AM GESCHENK BLOCKIEREN
     ============================================================ */

  function preventGiftQuantityChanges() {

    /*
      Klicks bereits in Capture-Phase stoppen.
    */

    document.addEventListener(
      'click',
      event => {

        const item =
          event.target.closest(
            `
            cart-item,
            .cart-item,
            tr.cart-item,
            [data-cart-item]
            `
          );


        if (
          !item ||
          !isGiftCartItem(item)
        ) {
          return;
        }


        const quantityControl =
          event.target.closest(
            `
            .quantity__button,
            button[name="plus"],
            button[name="minus"],
            quantity-input,
            .quantity,
            .quantity__input,
            input[type="number"]
            `
          );


        if (!quantityControl) {
          return;
        }


        event.preventDefault();

        event.stopPropagation();

        event.stopImmediatePropagation();

      },
      true
    );


    /*
      CHANGE ebenfalls verhindern.
    */

    document.addEventListener(
      'change',
      event => {

        const item =
          event.target.closest(
            `
            cart-item,
            .cart-item,
            tr.cart-item,
            [data-cart-item]
            `
          );


        if (
          !item ||
          !isGiftCartItem(item)
        ) {
          return;
        }


        if (
          event.target.matches(
            `
            .quantity__input,
            input[name="updates[]"],
            input[type="number"]
            `
          )
        ) {

          event.preventDefault();

          event.stopPropagation();

          event.target.value =
            1;


          window.setTimeout(
            syncFreeGift,
            50
          );

        }

      },
      true
    );

  }


  /* ============================================================
     CART STYLES
     ============================================================ */

  function addGiftCartStyles() {

    if (
      document.getElementById(
        'zt-free-gift-cart-styles'
      )
    ) {
      return;
    }


    const style =
      document.createElement(
        'style'
      );


    style.id =
      'zt-free-gift-cart-styles';


    style.textContent = `

      .zt-free-gift-cart-quantity {
        display: inline-flex;
        align-items: center;
        justify-content: center;

        height: 22px;

        padding: 0 8px;

        background: #FF6900;
        color: #FFFFFF;

        border-radius: 4px;

        white-space: nowrap;

        font-family: var(--font-body-family);

        font-size: 12px;
        font-weight: 700;

        line-height: 1;

        letter-spacing: 0.02em;
      }

    `;


    document.head.appendChild(
      style
    );

  }


  /* ============================================================
     HAUPTLOGIK
     ============================================================ */

  async function syncFreeGift() {

    if (isSyncing) {
      return;
    }


    isSyncing =
      true;


    try {

      let cart =
        await getCart();


      updateGiftBoxes(
        cart
      );


      lockGiftQuantityControls();


      const signature =
        getCartSignature(
          cart
        );


      /*
        Wichtig:
        Auch wenn sich der Cart nicht geändert hat,
        kontrollieren wir die UI weiterhin.
      */

      if (
        signature ===
        lastCartSignature
      ) {

        lockGiftQuantityControls();

        return;

      }


      lastCartSignature =
        signature;


      const cartValue =
        getQualifyingCartValue(
          cart
        );


      const qualifies =
        cartValue >=
        MINIMUM_CART_VALUE;


      const giftItems =
        getGiftItems(
          cart
        );


      /* ========================================================
         GESCHENK FEHLT
         ======================================================== */

      if (
        qualifies &&
        giftItems.length === 0
      ) {

        await addGift();


        cart =
          await getCart();


        lastCartSignature =
          getCartSignature(
            cart
          );


        updateGiftBoxes(
          cart
        );


        lockGiftQuantityControls();


        return;

      }


      /* ========================================================
         GESCHENK VORHANDEN
         ======================================================== */

      if (
        qualifies &&
        giftItems.length > 0
      ) {

        const changed =
          await normalizeGiftQuantity(
            giftItems
          );


        if (changed) {

          cart =
            await getCart();


          lastCartSignature =
            getCartSignature(
              cart
            );


          updateGiftBoxes(
            cart
          );

        }


        lockGiftQuantityControls();


        return;

      }


      /* ========================================================
         UNTER 25 €
         ======================================================== */

      if (
        !qualifies &&
        giftItems.length > 0
      ) {

        for (
          const giftItem
          of giftItems
        ) {

          await changeGiftLine(
            giftItem.key,
            0
          );

        }


        cart =
          await getCart();


        lastCartSignature =
          getCartSignature(
            cart
          );


        updateGiftBoxes(
          cart
        );

      }


    } catch (error) {

      console.error(
        '[ZT Gratisgeschenk]',
        error
      );


    } finally {

      isSyncing =
        false;

    }

  }


  /* ============================================================
     DOM BEOBACHTEN
     ============================================================ */

  function watchCartDom() {

    let timeout = null;


    const observer =
      new MutationObserver(
        () => {

          window.clearTimeout(
            timeout
          );


          timeout =
            window.setTimeout(
              () => {

                lockGiftQuantityControls();

              },
              50
            );

        }
      );


    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );

  }


  /* ============================================================
     INITIALISIERUNG
     ============================================================ */

  function start() {

    addGiftCartStyles();

    preventGiftQuantityChanges();

    lockGiftQuantityControls();

    watchCartDom();

    syncFreeGift();


    window.setInterval(
      syncFreeGift,
      CHECK_INTERVAL
    );


    document.addEventListener(
      'visibilitychange',
      () => {

        if (
          document.visibilityState ===
          'visible'
        ) {

          syncFreeGift();

        }

      }
    );


    window.addEventListener(
      'pageshow',
      () => {

        syncFreeGift();

      }
    );

  }


  /* ============================================================
     START
     ============================================================ */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      start
    );

  } else {

    start();

  }

})();